import { logStorageSnapshot } from './metrics-service';

const SNAPSHOT_DELAY_MS = 2500;

let isSnapshotScheduled = false;

async function settle(read) {
  try {
    return await read();
  } catch (error) {
    return { error: error?.message };
  }
}

async function mapToObject(keys, read) {
  const values = await Promise.all(keys.map((key) => read(key)));
  return Object.fromEntries(keys.map((key, index) => [key, values[index]]));
}

function prefixKeys(prefix, keys) {
  return keys.map((key) => `${prefix}/${key}`);
}

function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function readWebStorageKeys(storage) {
  if (!storage) {
    return [];
  }
  return Array.from({ length: storage.length }, (_, index) =>
    storage.key(index),
  );
}

async function readObjectStoreKeys(db, storeName) {
  const store = db.transaction(storeName, 'readonly').objectStore(storeName);
  const keys = await promisifyRequest(store.getAllKeys());
  return prefixKeys(storeName, keys);
}

async function readDatabaseKeys(name) {
  const db = await promisifyRequest(window.indexedDB.open(name));
  try {
    const storeKeys = await Promise.all(
      Array.from(db.objectStoreNames).map((storeName) =>
        readObjectStoreKeys(db, storeName),
      ),
    );
    return prefixKeys(name, storeKeys.flat());
  } finally {
    db.close();
  }
}

async function readIndexedDBKeys() {
  // indexedDB.databases() is missing in older Firefox and some embedded browsers.
  if (!window.indexedDB?.databases) {
    return [];
  }
  const databases = await window.indexedDB.databases();
  const databaseKeys = await Promise.all(
    databases.map(({ name }) =>
      readDatabaseKeys(name).catch((error) => [
        `${name}/<error: ${error?.message}>`,
      ]),
    ),
  );
  return databaseKeys.flat();
}

async function readCacheKeys(name) {
  const cache = await window.caches.open(name);
  const requests = await cache.keys();
  return prefixKeys(
    name,
    requests.map((request) => request.url),
  );
}

async function readCacheStorageKeys() {
  // window.caches is only exposed in secure contexts (HTTPS or localhost).
  if (!window.caches) {
    return [];
  }
  const cacheNames = await window.caches.keys();
  const cacheKeys = await Promise.all(cacheNames.map(readCacheKeys));
  return cacheKeys.flat();
}

const STORAGE_READERS = {
  localStorage: () => readWebStorageKeys(window.localStorage),
  sessionStorage: () => readWebStorageKeys(window.sessionStorage),
  indexedDB: readIndexedDBKeys,
  cacheStorage: readCacheStorageKeys,
};

function readAllStorages() {
  return mapToObject(Object.keys(STORAGE_READERS), (name) =>
    settle(STORAGE_READERS[name]),
  );
}

function scheduleStorageSnapshot() {
  if (isSnapshotScheduled) {
    return;
  }
  isSnapshotScheduled = true;
  // eslint-disable-next-line angular/timeout-service
  setTimeout(() => {
    readAllStorages()
      .then(logStorageSnapshot)
      .catch((error) => {
        console.error('Global Wrapper storage snapshot failed', error);
      });
  }, SNAPSHOT_DELAY_MS);
}

export { readAllStorages, scheduleStorageSnapshot };
